import { useCallback, useMemo, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { IconBell, IconLogout, IconUser } from '@tabler/icons-react';
import {
  Avatar,
  Button,
  EscrowBuildHeader,
  Footer,
  LOCALES,
  Menu,
  cn,
  useLocale,
  PRESS,
  type NotificationItem,
} from 'escrowbuild-ui';

import { useNotifications, useT } from '@/i18n';
import { displayName, type SessionUser } from '@/lib/auth';
import { useSession } from '@/lib/session';
import { NOTIFICATIONS } from '@/data/domain';
import { PLATFORM_NAV } from './nav';
import { usePageTitle } from './usePageTitle';

export function AppShell() {
  const navigate = useNavigate();
  const { status, user, signOut } = useSession();
  const { locale, setLocale } = useLocale();
  usePageTitle();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [read, setRead] = useState<ReadonlySet<string>>(
    () => new Set(NOTIFICATIONS.filter((entry) => entry.read).map((entry) => entry.id)),
  );

  const unreadCount = useMemo(
    () => NOTIFICATIONS.filter((entry) => !read.has(entry.id)).length,
    [read],
  );

  const openNotification = useCallback(
    (notification: NotificationItem) => {
      setRead((current) => new Set(current).add(notification.id));
      setNotificationsOpen(false);
      navigate('/notifications');
    },
    [navigate],
  );

  /*
     Leaving, in one place rather than per page.

     The platform's routes nest under this shell, so one gate here covers all of
     them — and a page added tomorrow is behind it without its author having to
     remember. It sits after every hook call, because a return before one would
     change the hook order between the signed-in and signed-out renders.

     `replace`, not a push: the route the visitor could not see must not be
     where "back" takes them.
  */
  if (status !== 'authenticated' || !user) return <Navigate to="/" replace />;

  const name = displayName(user);

  /*
     Sign-out, from anywhere in the platform's chrome.

     One handler for the header's menu and the sidebar's, so the two cannot
     drift. The session is already cleared by the time this returns — see
     `signOut` — and the navigation is what makes the exit visible even though
     the gate above would bounce the next render to `/` anyway.
  */
  function handleSignOut() {
    signOut();
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas text-body">
      <EscrowBuildHeader
        userName={name}
        avatarSrc={user.photo || undefined}
        homeHref="/dashboard"
        languages={LOCALES}
        language={locale}
        onLanguageChange={setLocale}
        onProfile={() => navigate('/permissions')}
        onSignOut={handleSignOut}
        unreadCount={unreadCount}
        notificationsOpen={notificationsOpen}
        onNotificationsClick={() => setNotificationsOpen((open) => !open)}
        notificationsPanel={
          notificationsOpen ? (
            <NotificationTray
              unreadCount={unreadCount}
              onOpenItem={openNotification}
              onMarkAllRead={() => setRead(new Set(NOTIFICATIONS.map((entry) => entry.id)))}
              onViewAll={() => {
                setNotificationsOpen(false);
                navigate('/notifications');
              }}
            />
          ) : null
        }
      />

      <div className="mx-auto flex w-full max-w-[1560px] flex-1 gap-8 px-4 py-6 sm:px-6">
        <Sidebar user={user} onSignOut={handleSignOut} />
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <Footer />
    </div>
  );
}

/**
 * The panel that drops from the bell.
 *
 * Deliberately not the full `NotificationsList` component: that one renders its
 * own bell trigger, so dropping it inside the header's panel would put two bells
 * on screen. This is the compact tray for the bar; the full list, with its
 * loading, error and retry states, is the `/notifications` page.
 */
function NotificationTray({
  unreadCount,
  onOpenItem,
  onMarkAllRead,
  onViewAll,
}: {
  unreadCount: number;
  onOpenItem: (notification: NotificationItem) => void;
  onMarkAllRead: () => void;
  onViewAll: () => void;
}) {
  /* The four most recent, in the reader's language: the panel shows a title and
     a line of body, and both are the record's own words. */
  const notifications = useNotifications();
  const preview = notifications.slice(0, 4);
  const t = useT();

  return (
    <div className="border-t border-line-subtle bg-surface-muted px-4 py-4 sm:px-6">
      <div className="mx-auto flex w-full max-w-[var(--container-wide)] flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p role="status" className="text-xs text-body">
            {unreadCount === 0
              ? t('shell.notifications.none')
              : t('shell.notifications.unread', { count: unreadCount })}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" disabled={unreadCount === 0} onClick={onMarkAllRead}>
              {t('shell.notifications.markAllRead')}
            </Button>
            <Button size="sm" variant="outline" onClick={onViewAll}>
              {t('shell.notifications.viewAll')}
            </Button>
          </div>
        </div>

        <ul className="flex list-none flex-col gap-1 p-0">
          {preview.length === 0 ? (
            <li className="flex items-center gap-2 py-2 text-xs text-muted">
              <IconBell size={14} aria-hidden="true" />
              {t('shell.notifications.nothing')}
            </li>
          ) : (
            preview.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => onOpenItem(entry)}
                  className={cn(
                    'flex w-full items-start gap-2.5 rounded-xs px-2 py-2 text-left',
                    'transition-colors duration-[var(--dur-fast)] hover:bg-surface',
                    PRESS,
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-pill',
                      entry.category === 'success'
                        ? 'bg-success'
                        : entry.category === 'warning'
                          ? 'bg-warning'
                          : entry.category === 'error'
                            ? 'bg-danger'
                            : 'bg-info',
                    )}
                  />
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'block truncate text-sm',
                        entry.read ? 'text-body' : 'font-medium text-heading',
                      )}
                    >
                      {entry.title}
                    </span>
                    <span className="block truncate text-xs text-muted">{entry.description}</span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function Sidebar({ user, onSignOut }: { user: SessionUser; onSignOut: () => void }) {
  const { pathname } = useLocation();
  const t = useT();
  const name = displayName(user);

  return (
    <aside aria-label={t('nav.section.platform')} className="hidden w-60 shrink-0 lg:block">
      <nav className="sticky top-24 flex flex-col gap-6">
        {PLATFORM_NAV.map((group) => (
          /*
             A group name, NOT a heading.

             These were `<h2>`, which put six navigation labels at the TOP of
             every page's document outline — measured on `/`, the outline ran
             Overview, Delivery, Money, Relationships, Publishing,
             Administration, and only then the page's own `h1`. The heading a
             reader meets first on the financials screen was the word "Money",
             from the menu, and the page's real name came seventh.

             `role="group"` with `aria-labelledby` keeps the grouping announced —
             a screen reader still hears these labels in the menu — without
             competing with the page for the document's structure. The span
             carries the id the group is named by, so the visible label and the
             announced one are the same string.
          */
          <div key={group.labelKey} role="group" aria-labelledby={`nav-group-${group.labelKey}`}>
            <span
              id={`nav-group-${group.labelKey}`}
              className="block px-2 font-display text-2xs font-semibold tracking-label text-muted uppercase"
            >
              {t(group.labelKey)}
            </span>
            <ul className="mt-2 flex list-none flex-col gap-0.5 p-0">
              {group.items.map((item) => {
                const active = item.end ? pathname === item.path : pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.end}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'group relative flex items-start gap-2.5 rounded-xs px-2 py-2 no-underline',
                        'transition-colors duration-[var(--dur-fast)]',
                        PRESS,
                        active
                          ? 'bg-surface-muted text-heading'
                          : 'text-body hover:bg-surface-muted hover:text-heading',
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                      )}
                    >
                      {/* The active marker is a shape as well as a colour, so the
                          state survives forced-colours mode. */}
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute top-1.5 -left-2 bottom-1.5 w-0.5 rounded-pill',
                          active ? 'bg-primary' : 'bg-transparent',
                        )}
                      />
                      <Icon
                        size={16}
                        aria-hidden="true"
                        className={cn('mt-0.5 shrink-0', active ? 'text-primary' : 'text-muted')}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{t(item.labelKey)}</span>
                        <span className="block truncate text-2xs text-muted">{t(item.hintKey)}</span>
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="border-t border-line-subtle pt-4">
          <Menu
            label={t('nav.account')}
            placement="bottom-start"
            items={[
              {
                id: 'account-identity',
                label: `${name} · ${user.email}`,
                icon: <IconUser size={14} aria-hidden="true" />,
              },
              {
                id: 'account-signout',
                label: t('nav.signOut'),
                icon: <IconLogout size={14} aria-hidden="true" />,
                onSelect: onSignOut,
              },
            ]}
            trigger={
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 rounded-xs px-2 py-2 text-left text-sm text-body',
                  'transition-colors duration-[var(--dur-fast)] hover:bg-surface-muted hover:text-heading',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {/*
                  `Avatar` and not a hand-rolled initials chip: it is the same
                  `--primary-subtle` ground with `text-heading` (the pair the
                  chip was chosen for, measured at 13.42:1), plus the provider's
                  photo when there is one — and its initials fallback IS the chip
                  this replaces. `aria-hidden` because the button already names
                  the account; the second line is the real email, where "Project
                  owner" was invented for the mockup.
                */}
                <Avatar
                  name={name}
                  src={user.photo || undefined}
                  size="sm"
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block truncate">{name}</span>
                  <span className="block truncate text-2xs text-muted">{user.email}</span>
                </span>
              </button>
            }
          />
        </div>
      </nav>
    </aside>
  );
}
